import { useMemo, useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

//* Helpers
import breakString from '@helpers/breakString';

//* Components
import Text from '@/components/global/Text';

//* Styles
import FaqStyle from './style';

const scrollTriggerDefaults = {
	start: 'top bottom',
	toggleActions: 'play none none reverse',
	invalidateOnRefresh: true,
};

const Faq = ({ faqs }) => {
	//! Refs
	const sectionRef = useRef();
	const titleRef = useRef();

	//! Animation — ScrollTrigger
	useLayoutEffect(() => {
		const section = sectionRef.current;
		if (!section) return;

		const ctx = gsap.context(() => {
			const titleAccent = titleRef.current?.querySelector('.faq-title-item-3');
			const titleTrigger = titleRef.current;
			const faqItems = section.querySelectorAll('.faq_item');

			if (titleTrigger) {
				const titleTl = gsap.timeline({ paused: true, defaults: { ease: 'power2.out', overwrite: 'auto' } });

				titleTl.fromTo(titleTrigger, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, delay: 0.05 });

				if (titleAccent) {
					titleTl.fromTo(titleAccent, { fontStyle: 'normal' }, { fontStyle: 'italic', duration: 0.2, ease: 'none' }, '>');
				}

				const snapHideTitle = () => {
					titleTl.pause(0);
					gsap.set(titleTrigger, { autoAlpha: 0 });
					if (titleAccent) gsap.set(titleAccent, { fontStyle: 'normal' });
				};

				ScrollTrigger.create({
					trigger: titleTrigger,
					start: scrollTriggerDefaults.start,
					invalidateOnRefresh: true,
					onEnter: () => titleTl.play(0),
					onLeaveBack: snapHideTitle,
				});
			}

			faqItems.forEach((item) => {
				gsap.fromTo(
					item,
					{ opacity: 0, y: 18 },
					{
						opacity: 1,
						y: 0,
						duration: 0.65,
						ease: 'power2.out',
						overwrite: 'auto',
						scrollTrigger: {
							...scrollTriggerDefaults,
							trigger: item,
						},
					}
				);
			});
		}, sectionRef);

		const ro = new ResizeObserver(() => requestAnimationFrame(() => ScrollTrigger.refresh()));
		ro.observe(document.body);

		return () => {
			ro.disconnect();
			ctx.revert();
		};
	}, [faqs]);

	//! Faqs
	const storeFaqs = useMemo(() => {
		return faqs.map((faq, index) => {
			return (
				<div
					key={index}
					className={'faq_item'}>
					<Text
						tag={'h4'}
						className={`h6 font-anonymous faq_number`}>
						{`${index + 1}`.padStart(2, 0)}
					</Text>

					<div className={'content'}>
						<Text className={`font-poppins font-regular title`}>{faq.title}</Text>

						<div className={`p font-poppins font-light description`}>
							<span
								className={`p font-poppins font-light description`}
								dangerouslySetInnerHTML={{ __html: faq.description }}
							/>
						</div>
					</div>
				</div>
			);
		});
	}, [faqs]);

	return (
		<FaqStyle
			ref={sectionRef}
			className={'faq_section'}>
			<Text
				tag={'h1'}
				ref={titleRef}
				className={`h2 font-poppins font-semi-bold faq_title`}>
				{breakString('faq', true, '', 'faq-title-item', true)}
			</Text>

			<div className={'faqs_wrap'}>{storeFaqs}</div>
		</FaqStyle>
	);
};

export default Faq;
